import React, { Fragment } from 'react';
import Select, { StylesConfig, createFilter } from 'react-select';
import cl from 'classnames';
import debounce from 'lodash/debounce';

import {
  MultiSelectChangeCallbackType,
  MultiSelectMixedDataType
} from './types';
import {
  MultiSelectProps,
  MultiSelectOptionalProps,
  MultiSelectValueType
} from './MultiSelect.types';

import { useMultiSelect } from './hooks/useMultiSelect';
import { useTranslate } from '../../common/hooks/useTranslate';

import { Translate } from '../Translate';
import { DropdownIndicator } from './components/DropdownIndicator';
import { MultiValue } from './components/MultiValue';
import { SingleValue } from './components/SingleValue';
import { Option } from './components/Option';
import { SortableMultiValue } from './components/SortableMultiValue';
import { SortableMultiValueRemove } from './components/SortableMultiValueRemove';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';

const multiSelectStyles: StylesConfig<
  MultiSelectMixedDataType | MultiSelectMixedDataType[] | null,
  boolean
> = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (styles) => ({ minHeight: 40, ...styles })
};

interface MultiSelectRequiredProps {
  onChange?: MultiSelectChangeCallbackType;
  value?: MultiSelectValueType;
}

function MultiSelect({
  autoFocus = false,
  blurInputOnSelect = false,
  classNamePrefix = null,
  closeMenuOnSelect = true,
  data = [],
  defaultValue = null,
  disabled = false,
  emptyValue = null,
  error,
  errorClassName,
  filterConfig,
  formatGroupLabel,
  formatOptionLabel,
  i18nLabel = null,
  i18nPlaceholder,
  inputWrapperClassName = null,
  isClearable = false,
  isLoading = false,
  isOptionDisabled,
  isSearchable = false,
  labelClassName,
  menuIsOpen,
  menuPlacement = 'auto',
  menuPosition,
  multi = false,
  name,
  onBlur,
  onChange,
  onInputChange,
  onMenuClose,
  onMenuOpen,
  onMenuScrollToBottom,
  openMenuOnFocus = false,
  optionsLoading = false,
  placeholder = null,
  showError = true,
  sortable,
  value = null,
  withPortal = false
}: MultiSelectProps & MultiSelectOptionalProps & MultiSelectRequiredProps) {
  const { t } = useTranslate();

  const {
    dataWithEmptyValue,
    handleFilterOption,
    handleKeyDown,
    handleMenuClose,
    handleMenuOpen,
    handleOptionsLoadingMessage,
    localizeValue,
    handleDragEnd,
    sortableData
  } = useMultiSelect({
    data,
    emptyValue,
    onMenuClose,
    onMenuOpen,
    optionsLoading,
    onChange,
    value
  });

  const inputView = (
    <Select<
      MultiSelectMixedDataType | MultiSelectMixedDataType[] | null,
      boolean
    >
      autoFocus={autoFocus}
      blurInputOnSelect={blurInputOnSelect}
      classNamePrefix={classNamePrefix}
      closeMenuOnSelect={closeMenuOnSelect}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      components={{
        DropdownIndicator,
        SingleValue,
        Option,
        ...(sortable
          ? {
              MultiValue: SortableMultiValue,
              MultiValueRemove: SortableMultiValueRemove
            }
          : {
              MultiValue
            })
      }}
      defaultValue={localizeValue(defaultValue)}
      filterOption={
        onInputChange
          ? handleFilterOption
          : filterConfig
          ? createFilter(filterConfig)
          : undefined
      }
      formatGroupLabel={formatGroupLabel}
      formatOptionLabel={formatOptionLabel}
      // hideSelectedOptions={false}
      isClearable={isClearable}
      isDisabled={disabled}
      isLoading={isLoading}
      isMulti={multi}
      isOptionDisabled={isOptionDisabled}
      isSearchable={isSearchable}
      menuIsOpen={menuIsOpen}
      menuPlacement={menuPlacement}
      menuPortalTarget={withPortal ? document.body : undefined}
      menuPosition={menuPosition}
      name={name}
      noOptionsMessage={handleOptionsLoadingMessage}
      onBlur={onBlur}
      onChange={onChange}
      onInputChange={onInputChange ? debounce(onInputChange, 500) : undefined}
      onKeyDown={handleKeyDown}
      onMenuClose={handleMenuClose}
      onMenuOpen={handleMenuOpen}
      onMenuScrollToBottom={onMenuScrollToBottom}
      openMenuOnFocus={openMenuOnFocus}
      options={dataWithEmptyValue}
      placeholder={i18nPlaceholder ? t(i18nPlaceholder) : placeholder}
      styles={multiSelectStyles}
      value={value}
    />
  );

  if (inputWrapperClassName) {
    return (
      <Fragment>
        {i18nLabel ? (
          <label htmlFor={name} className={labelClassName}>
            <Translate id={i18nLabel} />
          </label>
        ) : null}

        <div>
          <div className={inputWrapperClassName}>
            {sortable ? (
              <DndContext
                modifiers={[restrictToParentElement]}
                onDragOver={handleDragEnd}
                collisionDetection={closestCenter}
              >
                <SortableContext items={sortableData} strategy={() => null}>
                  {inputView}
                </SortableContext>
              </DndContext>
            ) : (
              inputView
            )}
          </div>

          {error && showError && (
            <p className={cl(errorClassName || 'mt-2 text-sm text-red-600')}>
              {/^forms\.errors+/.test(error) ? <Translate id={error} /> : error}
            </p>
          )}
        </div>
      </Fragment>
    );
  }

  return inputView;
}

export default MultiSelect;
